import type { ElementType } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { alpha, SvgIconProps, Unstable_Grid2 as Grid } from '@mui/material';
import { FieldArray } from 'react-final-form-arrays';
import { Field, useForm, useFormState } from 'react-final-form';

import {
  AddIcon,
  Button,
  DeleteForeverOutlinedIcon,
  IconButton,
  Switch,
  TextField,
  Select,
} from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { parseNumber } from '@occc-app/lib/finalFormUtils';

import type {
  AwardCountLimitPeriodOption,
  AwardCountLimitRow,
  AwardCountLimitTimePeriod,
} from '../../award-count-limit';
import { awardCountPeriodOptions } from '../../award-count-limit';
import {
  getFormValueByPath,
  getNextAvailablePeriod,
  getNormalizedTransactionFrequencyFormValue,
  hasValidTransactionFrequencyRowsArray,
} from '../../transaction-frequency-limit/transaction-frequency-limit.utils';
import { LimitFormContentLayout } from '../limit-form-content-layout';

type RowFieldSuffix = '.limit' | '.timePeriod';

type StackedLimitRowsProps = {
  formNamePath: string;
  open: boolean;
  title: string;
  description: string;
  limitLabel?: string;
  avatar?: ElementType<SvgIconProps>;
  periodOptions?: AwardCountLimitPeriodOption[];
  defaultPeriod?: AwardCountLimitTimePeriod;
};

export const StackedLimitRows = ({
  formNamePath,
  open,
  title,
  description,
  limitLabel,
  avatar,
  periodOptions = awardCountPeriodOptions,
  defaultPeriod: defaultPeriodProp,
}: StackedLimitRowsProps) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const formValue = getFormValueByPath(values, formNamePath);
  const hasValidRowsArray = hasValidTransactionFrequencyRowsArray(formValue);
  const rows: AwardCountLimitRow[] = useMemo(
    () => (formValue && formValue.isEnabled && 'rows' in formValue ? formValue.rows : []),
    [formValue],
  );
  const { isEditable } = useCanEditFormFieldsContext();
  const defaultPeriod = defaultPeriodProp ?? periodOptions[0]?.value ?? 'TOTAL';

  useEffect(() => {
    if (hasValidRowsArray) return;
    const normalized = getNormalizedTransactionFrequencyFormValue(
      form.getState().values,
      formNamePath,
    );
    form.change(formNamePath, normalized);
  }, [form, hasValidRowsArray, formNamePath]);

  const ensureDefaultRowWhenEnabling = useCallback(() => {
    const current = getFormValueByPath(form.getState().values, formNamePath);
    const currentRows = hasValidTransactionFrequencyRowsArray(current) ? current.rows : [];
    if (currentRows.length === 0) {
      form.change(`${formNamePath}.rows`, [{ limit: 1, timePeriod: defaultPeriod }]);
    }
  }, [form, formNamePath, defaultPeriod]);

  useEffect(() => {
    const current = getFormValueByPath(form.getState().values, formNamePath);
    if (!current?.isEnabled || !('rows' in current)) return;

    current.rows.forEach((row, i) => {
      const isAllowed = periodOptions.some((o) => o.value === row.timePeriod);
      if (!isAllowed) {
        form.change(`${formNamePath}.rows[${i}].timePeriod`, defaultPeriod);
      }
    });
  }, [form, formNamePath, periodOptions, defaultPeriod, rows.length]);

  return (
    <LimitFormContentLayout
      title={title}
      description={description}
      action={
        <Field name={`${formNamePath}.isEnabled`} type='checkbox'>
          {({ input }) => (
            <Switch
              name={input.name}
              checked={Boolean(input.checked)}
              onChange={(e) => {
                input.onChange(e);
                if (e.target.checked) {
                  ensureDefaultRowWhenEnabling();
                }
              }}
              disabled={!isEditable}
            />
          )}
        </Field>
      }
      open={open}
      avatar={avatar}
    >
      {hasValidRowsArray && (
        <FieldArray name={`${formNamePath}.rows`}>
          {({ fields }) => (
            <>
              {(fields.length ?? 0) === 0 && (
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant='contained'
                      startIcon={<AddIcon />}
                      onClick={() => fields.push({ limit: 1, timePeriod: defaultPeriod })}
                      sx={(theme) => ({
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: theme.palette.primary.main,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.25) },
                      })}
                    >
                      ADD LIMIT
                    </Button>
                  </Grid>
                </Grid>
              )}
              {fields.map((name, index) => {
                const canRemove = (fields.length ?? 0) > 1;
                const isLastRow = index === (fields.length ?? 1) - 1;
                const canAddMore = (fields.length ?? 0) < periodOptions.length;
                const showAddButtonInRow = isLastRow && canAddMore;
                const addButtonSize = canRemove ? 5 : 6;

                return (
                  <Grid key={name} container spacing={3} sx={{ mb: 2 }} alignItems='flex-start'>
                    <Grid xs={12} sm={3}>
                      <TextField
                        name={`${name}${'.limit' satisfies RowFieldSuffix}`}
                        label={limitLabel || 'Times'}
                        fieldProps={{ parse: parseNumber }}
                      />
                    </Grid>
                    <Grid xs={12} sm={3}>
                      <Select
                        name={`${name}${'.timePeriod' satisfies RowFieldSuffix}`}
                        label='Frequency'
                        data={periodOptions}
                        formControlProps={{ size: 'medium', margin: 'none' }}
                      />
                    </Grid>
                    {canRemove && (
                      <Grid xs={12} sm={1}>
                        <IconButton
                          onClick={() => fields.remove(index)}
                          size='medium'
                          aria-label='Remove limit'
                          sx={(theme) => ({
                            bgcolor: alpha(theme.palette.error.main, 0.2),
                            borderRadius: 1,
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.25) },
                          })}
                        >
                          <DeleteForeverOutlinedIcon color='error' fontSize='inherit' />
                        </IconButton>
                      </Grid>
                    )}
                    {showAddButtonInRow && (
                      <Grid
                        xs={12}
                        sm={addButtonSize}
                        sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      >
                        <Button
                          variant='contained'
                          startIcon={<AddIcon />}
                          onClick={() => {
                            fields.push({
                              limit: 1,
                              timePeriod: getNextAvailablePeriod(
                                rows,
                                periodOptions,
                                defaultPeriod,
                              ),
                            } satisfies AwardCountLimitRow);
                          }}
                          sx={(theme) => ({
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.25) },
                          })}
                        >
                          ADD ANOTHER LIMIT
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                );
              })}
            </>
          )}
        </FieldArray>
      )}
    </LimitFormContentLayout>
  );
};
